import React, { useEffect, useMemo } from "react";
import useSongStore from "../store/SongState";
import { SongCard } from "./song-card";
import { Song } from "../types";
import { ScrollModalApi } from "../../../shared/scroll-modal/view";
import { SongOptionsModal } from "./song-options-modal";
import { Spinner } from "react-bootstrap";
import { Spotip17anFunctions } from "../../../../utils/api";
import useUserStore from "../../../../store/UserState";
import './song-list.scss';
import { filterList } from "../../../../utils/utils";
import { OptionButton } from "../../../shared/option-button";
import SeparatorLine from "../../../shared/separator-line/separator-line";
import { isSongInDB } from "../../../../utils/dbUtils";

type SongListViewProps = {
    songs: Song[];
    loadingSongs: boolean;
    canScrollToQueue?: boolean;
    showSearch?: boolean;
    showIndex?: boolean;
    style?: React.CSSProperties;
    markDownloaded?: boolean;
    extraOptions?: {
        text: string;
        icon: React.ReactNode;
        onClick: (song: Song, scrollModalApi: ScrollModalApi) => void;
    }[];
    songCardHeight?: number;
    onSongDeleted?: (songId: string | number) => void;
    onSongUpdated?: (song: Song) => void;
}

const SongListView = ({
    showSearch,
    showIndex,
    songs,
    loadingSongs,
    canScrollToQueue,
    style,
    markDownloaded,
    extraOptions,
    songCardHeight,
    onSongDeleted,
    onSongUpdated
}: SongListViewProps) => {
    const { setCurrentSong, addSongToQueue } = useSongStore(state => state);
    const [songOptions, setSongOptions] = React.useState<Song | null>(null);
    const scrollModalApi = React.useRef<ScrollModalApi>(null);
    const { user, offlineMode } = useUserStore(state => state.state);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [favoriteIds, setFavoriteIds] = React.useState<string[]>([]);

    const [thisSongs, setThisSongs] = React.useState<Song[]>([]);

    useEffect(() => {
        if (!songs?.length) return;

        if (!user?.uid || !favoriteIds.length) {
            setThisSongs([...songs]);
            return;
        }

        const updateSongs = async () => {
            setThisSongs(await Promise.all([...songs.map(async (song) => {
                song.is_favorite = favoriteIds.includes(song.id + "");
                const songDownloaded = await isSongInDB(song.id + "");
                song.download_status = songDownloaded ? "downloaded" : song.download_status;
                return song;
            })]));
        };

        updateSongs();

    }, [songs, user, favoriteIds]);

    useEffect(() => {
        if (!user?.uid || offlineMode) return;

        Spotip17anFunctions.use('findFavoritesIds').get().then((res: string[]) => {
            setFavoriteIds(res);
        });
    }, [user]);

    const filteredSongs = useMemo(() => {
        return filterList([...thisSongs], searchQuery, (song: Song) => song.name);
    }, [thisSongs, searchQuery]);

    if (loadingSongs) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
            }}>
                <Spinner />
            </div>
        );
    }

    if (!songs?.length) {
        return (
            <div>
                No hay canciones
            </div>
        );
    }

    const handleOptions = (e: React.MouseEvent, song: Song) => {
        e.stopPropagation();
        setSongOptions(song);
        scrollModalApi.current!.open({
            x: e.clientX,
            y: e.clientY
        });
    }

    const handleModifySelectedSong = (song: Song) => {
        setThisSongs([...thisSongs.map(s => String(s.id) === String(song.id) ? song : s)]);
        onSongUpdated?.(song);
    };

    const handleDeleteSelectedSong = (songId: string | number) => {
        setThisSongs([...thisSongs.filter(s => String(s.id) !== String(songId))]);
        onSongDeleted?.(songId);
    };

    return (
        <>
            {showSearch &&
                <div className="song-list__search-container">
                    <div className="mb-3">
                        <input type="text" placeholder="Buscar en la playlist" className="form-control"
                            id="searchSongList" autoComplete={"off"} value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)} />
                    </div>
                </div>

            }
            <div className="songs-container" style={style}>
                {filteredSongs.map((song, index) => (
                    <div key={song.id + "-" + index} className="d-flex align-items-center" style={{ width: "100%" }}>
                        {showIndex && (
                            <span
                                className="text-muted fw-bold text-center me-2"
                                style={{ width: 24, fontSize: '1rem', flexShrink: 0 }}
                            >
                                {song.track_number ?? (index + 1)}
                            </span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <SongCard
                                onScroll={canScrollToQueue ? addSongToQueue : undefined} height={songCardHeight}
                                onClick={setCurrentSong} indicateQueue={true} ableHovering={true} onOptions={handleOptions}
                                song={song} markDownloaded={markDownloaded && song.saved} />
                        </div>
                    </div>
                ))}
            </div>
            <SongOptionsModal
                scrollRef={scrollModalApi} ref={scrollModalApi} setSong={handleModifySelectedSong}
                onDeleteSong={handleDeleteSelectedSong}
                song={songOptions}
                extraOptions={!!extraOptions?.length && <>
                    <SeparatorLine />
                    {
                        extraOptions.map(option => {
                            return <OptionButton hidden={offlineMode} key={option.text} text={option.text} icon={option.icon}
                                onClick={() => option.onClick(songOptions!, scrollModalApi.current!)} />
                        })
                    }
                </>}
            />
        </>
    );
}

export default SongListView;
